// src/pages/FlightSheetForm.js - Update
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Card,
    Form,
    Button,
    Alert,
    Spinner,
    Tabs,
    Tab,
    InputGroup
} from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useTranslation } from 'react-i18next';
import PoolSelector from '../components/PoolSelector'; // Import the new component

const FlightSheetForm = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { t } = useTranslation();

    const [formData, setFormData] = useState({
        name: '',
        poolHost: '',
        poolPort: '3333',
        poolWorker: '',
        poolPassword: 'x'
    });

    const [devFee1, setDevFee1] = useState({
        enabled: false,
        percentage: 1.0,
        poolHost: '',
        poolPort: 3333,
        poolWorker: '',
        poolPassword: 'x'
    });

    // Add state for pool selection
    const [useCustomIdentifier, setUseCustomIdentifier] = useState(false);
    const [customIdentifier, setCustomIdentifier] = useState('');
    const [identifierError, setIdentifierError] = useState('');
    const [showPoolFields, setShowPoolFields] = useState(true); // New state to control field visibility

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [generatedWorker, setGeneratedWorker] = useState('');
    const [activeTab, setActiveTab] = useState('main');

    const { name, poolHost, poolPort, poolWorker, poolPassword } = formData;

    // Function to handle pool selection
    const handlePoolSelect = (host, port) => {
        setFormData({
            ...formData,
            poolHost: host,
            poolPort: port
        });
    };

    // Validation function for custom identifier
    const validateIdentifier = (value) => {
        if (useCustomIdentifier && !value.trim()) {
            setIdentifierError(t('flightSheet.minerIdInfo'));
            return false;
        }

        if (value.length > 12) {
            setIdentifierError(t('flightSheet.minerIdInfo'));
            return false;
        }

        if (!/^[a-zA-Z0-9]+$/.test(value)) {
            setIdentifierError(t('flightSheet.minerIdInfo'));
            return false;
        }

        setIdentifierError('');
        return true;
    };

    const onChange = e => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const onDevFee1Change = (field, value) => {
        setDevFee1({
            ...devFee1,
            [field]: field === 'enabled' ? (value === 'true' || value === true) : value
        });
    };

    const onSubmit = async e => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        // Form validation
        if (!name || !poolHost || !poolPort || !poolWorker) {
            setError(t('auth.fillAllFields'));
            setLoading(false);
            return;
        }

        // Validate custom identifier
        if (useCustomIdentifier && !validateIdentifier(customIdentifier)) {
            setLoading(false);
            return;
        }

        // DevFee validation
        if (devFee1.enabled) {
            if (!devFee1.poolHost || !devFee1.poolWorker) {
                setError(t('auth.fillAllFields'));
                setActiveTab('devfee1');
                setLoading(false);
                return;
            }

            if (devFee1.percentage < 1 || devFee1.percentage > 5) {
                setError(t('devfee.percentage'));
                setActiveTab('devfee1');
                setLoading(false);
                return;
            }
        }

        try {
            // Prepare data for submission
            const submitData = {
                ...formData
            };

            // Add custom identifier if enabled
            if (useCustomIdentifier) {
                submitData.minerWorker = customIdentifier;
            }

            // Include DevFee if enabled
            if (devFee1.enabled) {
                submitData.devFee1 = devFee1;
            }

            console.log("Submitting data:", submitData);

            const response = await api.post('/api/flight-sheets', submitData);

            // Save the generated minerWorker
            setGeneratedWorker(response.data.data.minerWorker);

            setSuccess(t('flightSheet.createSuccess'));
            setLoading(false);

            // Redirect to flight sheet page after 2 seconds
            setTimeout(() => {
                navigate(`/flight-sheets/${response.data.data._id}`);
            }, 2000);
        } catch (err) {
            console.error("Error creating flight sheet:", err);
            setError(
                err.response?.data?.message ||
                t('errors.createError')
            );
            setLoading(false);
        }
    };

    return (
        <Container className="my-4">
            <h1 className="mb-4">{t('flightSheet.new')}</h1>

            <Card className="shadow-sm">
                <Card.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    {success && (
                        <div className="miner-id-notification">
                            <h4>{success}</h4>
                            {generatedWorker && (
                                <div>
                                    <span className="miner-id-label">{t('flightSheet.minerIdentifier')}:</span>
                                    <div className="miner-id-value">{generatedWorker}</div>
                                    <p className="miner-id-hint">
                                        {t('flightSheet.useMinerID')}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    <Tabs
                        activeKey={activeTab}
                        onSelect={(k) => setActiveTab(k)}
                        className="mb-4"
                    >
                        <Tab eventKey="main" title={t('flightSheet.mainSettings')}>
                            <Form onSubmit={onSubmit} className="mt-3">
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('flightSheet.name')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={name}
                                        onChange={onChange}
                                        placeholder={t('flightSheet.enterName')}
                                        required
                                    />
                                    <Form.Text className="text-muted">
                                        {t('flightSheet.nameInList')}
                                    </Form.Text>
                                </Form.Group>

                                {/* Add custom identifier toggle */}
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="switch"
                                        id="custom-identifier"
                                        label={t('flightSheet.customMinerId')}
                                        checked={useCustomIdentifier}
                                        onChange={(e) => setUseCustomIdentifier(e.target.checked)}
                                    />
                                    <Form.Text className="text-muted">
                                        {t('flightSheet.mineWithDefault')}
                                    </Form.Text>
                                </Form.Group>

                                {useCustomIdentifier && (
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('flightSheet.minerId')}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={customIdentifier}
                                            onChange={(e) => {
                                                setCustomIdentifier(e.target.value);
                                                validateIdentifier(e.target.value);
                                            }}
                                            placeholder={t('flightSheet.minerIdInfo')}
                                            isInvalid={!!identifierError}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {identifierError}
                                        </Form.Control.Feedback>
                                        <Form.Text className="text-muted">
                                            {t('flightSheet.minerIdInfo')}
                                        </Form.Text>
                                    </Form.Group>
                                )}

                                <h5 className="mb-3">{t('flightSheet.poolSettings')}</h5>

                                {/* Add the PoolSelector component here */}
                                <PoolSelector
                                    onSelectPool={handlePoolSelect}
                                    onShowFields={setShowPoolFields}
                                    showAddressTag={!showPoolFields}
                                />

                                {showPoolFields && (
                                    <>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('flightSheet.poolHost')}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="poolHost"
                                                value={poolHost}
                                                onChange={onChange}
                                                placeholder={t('flightSheet.enterPoolHost')}
                                                required
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('flightSheet.poolPort')}</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="poolPort"
                                                value={poolPort}
                                                onChange={onChange}
                                                placeholder={t('flightSheet.poolPort')}
                                                required
                                            />
                                        </Form.Group>
                                    </>
                                )}

                                <Form.Group className="mb-3">
                                    <Form.Label>{t('flightSheet.poolWorker')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="poolWorker"
                                        value={poolWorker}
                                        onChange={onChange}
                                        placeholder={t('flightSheet.enterPoolWorker')}
                                        required
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>{t('flightSheet.poolPassword')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="poolPassword"
                                        value={poolPassword}
                                        onChange={onChange}
                                        placeholder={t('flightSheet.enterPoolPassword')}
                                    />
                                    <Form.Text className="text-muted">
                                        {t('flightSheet.passwordInfo')}
                                    </Form.Text>
                                </Form.Group>

                                <div className="mt-4 mb-3 d-flex justify-content-between">
                                    <Button
                                        variant="outline-primary"
                                        onClick={() => setActiveTab('devfee1')}
                                        type="button"
                                    >
                                        {t('devfee.settings')} 1
                                    </Button>
                                </div>
                            </Form>
                        </Tab>

                        {/* DevFee tab remains unchanged */}
                        <Tab eventKey="devfee1" title="DevFee 1">
                            <Form className="mt-3">
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="switch"
                                        id="enable-devfee1"
                                        label={t('devfee.enable') + " 1"}
                                        checked={devFee1.enabled}
                                        onChange={(e) => onDevFee1Change('enabled', e.target.checked)}
                                    />
                                    <Form.Text className="text-muted">
                                        {t('devfee.info')}
                                    </Form.Text>
                                </Form.Group>

                                {devFee1.enabled && (
                                    <>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('devfee.percentage')}</Form.Label>
                                            <InputGroup>
                                                <Form.Control
                                                    type="number"
                                                    min="1"
                                                    max="5"
                                                    step="0.1"
                                                    value={devFee1.percentage}
                                                    onChange={(e) => onDevFee1Change('percentage', e.target.value)}
                                                    required={devFee1.enabled}
                                                />
                                                <InputGroup.Text>%</InputGroup.Text>
                                            </InputGroup>
                                        </Form.Group>

                                        <h5 className="mb-3">{t('devfee.poolSettings')}</h5>

                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('flightSheet.poolHost')}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={devFee1.poolHost}
                                                onChange={(e) => onDevFee1Change('poolHost', e.target.value)}
                                                placeholder={t('flightSheet.enterPoolHost')}
                                                required={devFee1.enabled}
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('flightSheet.poolPort')}</Form.Label>
                                            <Form.Control
                                                type="number"
                                                value={devFee1.poolPort}
                                                onChange={(e) => onDevFee1Change('poolPort', e.target.value)}
                                                placeholder={t('flightSheet.poolPort')}
                                                required={devFee1.enabled}
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('flightSheet.poolWorker')}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={devFee1.poolWorker}
                                                onChange={(e) => onDevFee1Change('poolWorker', e.target.value)}
                                                placeholder={t('flightSheet.enterPoolWorker')}
                                                required={devFee1.enabled}
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('flightSheet.poolPassword')}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={devFee1.poolPassword}
                                                onChange={(e) => onDevFee1Change('poolPassword', e.target.value)}
                                                placeholder={t('flightSheet.enterPoolPassword')}
                                            />
                                        </Form.Group>
                                    </>
                                )}

                                <div className="mt-4 mb-3 d-flex justify-content-between">
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => setActiveTab('main')}
                                        type="button"
                                    >
                                        {t('common.back')}
                                    </Button>
                                </div>
                            </Form>
                        </Tab>
                    </Tabs>

                    <div className="mt-4 text-center">
                        <Button
                            variant="primary"
                            onClick={onSubmit}
                            className="w-100"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="me-2"
                                    />
                                    {t('common.loading')}
                                </>
                            ) : (
                                t('common.create')
                            )}
                        </Button>
                    </div>
                </Card.Body>
            </Card>

            <div className="mt-3 text-center">
                <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/dashboard')}
                >
                    {t('common.back')}
                </Button>
            </div>
        </Container>
    );
};

export default FlightSheetForm;